import React, { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { LogOut, LayoutDashboard, FileText, Plus, Users, Settings, Inbox, CheckSquare, BarChart2, Menu, ExternalLink, UserCircle, Megaphone, HardDrive, Activity, Moon, Sun, Bookmark, CreditCard } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import InspiraFooter from './InspiraFooter';
import './DashboardLayout.css';

const NAV = {
  reader: [
    { to: '/dashboard', icon: <LayoutDashboard size={16} />, label: 'Dashboard' },
    { to: '/artikel-saya', icon: <FileText size={16} />, label: 'Artikel Saya' },
    { to: '/publikasi', icon: <Plus size={16} />, label: 'Kirim Artikel' },
    { to: '/pasang-iklan', icon: <Megaphone size={16} />, label: 'Pasang Iklan' },
    { to: '/iklan-saya', icon: <FileText size={16} />, label: 'Iklan Saya' },
  ],
  editor: [
    { to: '/editor', icon: <Inbox size={16} />, label: 'Antrian Artikel' },
  ],
  producer: [
    { to: '/producer', icon: <CheckSquare size={16} />, label: 'Review Artikel' },
  ],
  admin: [
    { to: '/admin', icon: <BarChart2 size={16} />, label: 'Dashboard' },
    { to: '/admin/users', icon: <Users size={16} />, label: 'Kelola User' },
    { to: '/admin/articles', icon: <FileText size={16} />, label: 'Semua Artikel' },
    { to: '/admin/aktivitas', icon: <Activity size={16} />, label: 'Log Aktivitas' },
    { to: '/admin/pembayaran', icon: <CreditCard size={16} />, label: 'Verifikasi Pembayaran' },
    { to: '/admin/categories', icon: <Settings size={16} />, label: 'Kategori Topik' },
    { to: '/admin/banner',   icon: <Megaphone size={16} />, label: 'Banner & Iklan' },
    { to: '/admin/settings', icon: <Settings size={16} />,  label: 'Pengaturan Situs' },
    { to: '/admin/tata-letak', icon: <LayoutDashboard size={16} />, label: 'Tata Letak Portal' },
    { to: '/admin/storage',  icon: <HardDrive size={16} />, label: 'Manajemen Storage' },
  ],
};

export default function DashboardLayout({ children, title, subtitle }) {
  const { user, logout, updateUser } = useAuth();
  const { settings } = useSiteSettings();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  let links = NAV[user?.role] || NAV.reader;
  // Sembunyikan menu iklan reader jika fitur pasang iklan dinonaktifkan admin
  if (settings.featureAdSubmission === false) {
    links = links.filter(l => l.to !== '/pasang-iklan' && l.to !== '/iklan-saya');
  }
  const ownedRoles = (user?.roles && user.roles.length > 1) ? user.roles : null;

  const handleLogout = () => { logout(); navigate('/'); window.scrollTo({ top: 0 }); };

  const switchRole = async (r) => {
    if (r === user?.role) return;
    try {
      const res = await API.patch('/users/me/active-role', { role: r });
      updateUser(res.data.user);
      const dest = r === 'reader' ? '/dashboard' : `/${r}`;
      navigate(dest);
    } catch (err) { toast.error(err.response?.data?.message || 'Gagal mengganti role'); }
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="sidebar-logo">
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          {(theme === 'dark' && settings.logoSidebarDarkUrl ? settings.logoSidebarDarkUrl : (settings.logoSidebarUrl || settings.logoDarkUrl || settings.logoUrl))
            ? <img src={theme === 'dark' && settings.logoSidebarDarkUrl ? settings.logoSidebarDarkUrl : (settings.logoSidebarUrl || settings.logoDarkUrl || settings.logoUrl)} alt={settings.siteName} style={{ height: settings.logoSidebarSize || 36, objectFit: 'contain' }} />
            : <>
                <img
                  src="/logo-inspira-white.png"
                  alt={settings.siteName}
                  style={{ height: settings.logoSidebarSize || 36, objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <span style={{
                  display: 'none', alignItems: 'center', gap: 8,
                  fontWeight: 900, fontSize: 16, color: '#fff',
                }}>
                  <span style={{
                    width: 30, height: 30, borderRadius: 7, background: 'var(--in-orange)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 900, color: '#fff', flexShrink: 0,
                  }}>IN</span>
                  {settings.siteName}
                </span>
              </>
          }
        </Link>
      </div>

      {/* Nav */}
      <div className="sidebar-nav">
        <div className="sidebar-section">Menu Utama</div>
        {links.map(l => (
          <NavLink
            key={l.to} to={l.to} end
            className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
          >
            {l.icon} {l.label}
          </NavLink>
        ))}

        <div className="sidebar-section" style={{ marginTop: 12 }}>Akun</div>
        <NavLink
          to="/profil"
          className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
        >
          <UserCircle size={16} /> Profil Saya
        </NavLink>
        <NavLink
          to="/bookmark"
          className={({ isActive }) => 'sidebar-link' + (isActive ? ' active' : '')}
        >
          <Bookmark size={16} /> Bookmark Saya
        </NavLink>
        <a href="/" className="sidebar-link">
          <ExternalLink size={15} /> Lihat Portal
        </a>
        <button className="sidebar-link" onClick={toggleTheme} style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', font: 'inherit' }}>
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />} {theme === 'dark' ? 'Mode Terang' : 'Mode Gelap'}
        </button>
      </div>

      {/* User bottom */}
      <div className="sidebar-bottom">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          {user?.avatar
            ? <img src={user.avatar} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            : <div style={{
                width: 34, height: 34, borderRadius: '50%',
                background: 'var(--in-orange)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, color: 'white', flexShrink: 0,
              }}>{user?.name?.[0]}</div>
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'capitalize' }}>{user?.role}</div>
          </div>
        </div>
        {ownedRoles && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 6 }}>Ganti Peran</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {ownedRoles.map(r => (
                <button key={r} onClick={() => switchRole(r)}
                  style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize',
                    background: r === user?.role ? 'var(--in-orange)' : 'rgba(255,255,255,0.08)',
                    color: r === user?.role ? '#fff' : 'rgba(255,255,255,0.7)',
                    border: '1px solid ' + (r === user?.role ? 'var(--in-orange)' : 'rgba(255,255,255,0.12)') }}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="btn btn-sm btn-full"
          style={{ justifyContent: 'flex-start', gap: 8, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6 }}
        >
          <LogOut size={14} /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="dash-root">
      {/* Sidebar desktop */}
      <aside className={`dash-sidebar ${mobileOpen ? 'open' : ''}`}>
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99, backdropFilter: 'blur(2px)' }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main */}
      <div className="dash-main">
        <div className="dash-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="btn btn-icon btn-ghost dash-menu-btn"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <Menu size={18} />
            </button>
            <div style={{ fontSize: 13, color: 'var(--in-text-muted)' }}>
              <Link to="/" style={{ color: 'var(--in-text-faint)', textDecoration: 'none' }}>{settings.siteName}</Link>
              <span style={{ margin: '0 6px', color: 'var(--in-border)' }}>/</span>
              <span style={{ textTransform: 'capitalize', color: 'var(--in-text-muted)' }}>{user?.role}</span>
            </div>
          </div>
          {!user?.isEmailVerified && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '4px 12px', fontSize: 12, color: '#92400E' }}>
              Email belum diverifikasi
            </div>
          )}
        </div>

        <div className="dash-content">
          {title && <div className="dash-page-title">{title}</div>}
          {subtitle && <div className="dash-page-sub">{subtitle}</div>}
          {children}
        </div>

        {/* Footer di dashboard */}
        <div style={{
          margin: '40px 32px 0',
          padding: '16px 0',
          borderTop: '1px solid var(--in-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 8, fontSize: 12, color: 'var(--in-text-faint)',
        }}>
          <span>© {new Date().getFullYear()} {settings.siteName} · {settings.copyrightText}</span>
          <div style={{ display: 'flex', gap: 16 }}>
            {(settings.externalLinks || []).map(l => (
              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--in-text-faint)', textDecoration: 'none' }}>{l.label}</a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
